import React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { DraftDocument } from '@graphql/platform';

import { WWW_ROUTES } from '@root/root/routes';

import { FunnelEntryPoint } from '@shared/funnel_entry_point';
import { FunnelEntryPointForm } from '@shared/funnel_entry_point_form';
import { FunnelEntryPointZipInput } from '@shared/funnel_entry_point_zip_input';
import { ServiceSelection } from '@shared/service_selector/constants';
import { useFunnelEntryPointContext } from '@shared/use_funnel_entry_point_context';

import { hasHomeDraft, useDraftResume } from './draft_resume';
import { scrollToY } from './lenis_scroll';
import { PrimaryButton } from './styles';
import { MEDIA, TOKENS } from './tokens';

type HomeZipBarVariant = 'hero' | 'footer';
type HomeZipBarSurface = 'light' | 'dark';

const ZipBarShell = styled.div<{ $compact?: boolean }>`
  width: 100%;
  max-width: 100%;
  min-width: 0;

  ${MEDIA.md} {
    max-width: ${({ $compact }) => ($compact ? '333px' : '364px')};
  }
`;

const ZipBar = styled.div<{
  $compact?: boolean;
  $hasError?: boolean;
  $attachedError?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: stretch;
  gap: ${TOKENS.space[8]};
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border-radius: ${({ $attachedError }) =>
    $attachedError
      ? `${TOKENS.radius.md} ${TOKENS.radius.md} 0 0`
      : TOKENS.radius.md};
  border: 1px solid
    ${({ $hasError }) => ($hasError ? TOKENS.color.error : 'transparent')};
  background: ${TOKENS.color.background};
  padding: ${TOKENS.space[4]} ${TOKENS.space[4]} ${TOKENS.space[4]}
    ${({ $compact }) => ($compact ? TOKENS.space[16] : TOKENS.space[24])};

  && input[type='tel'] {
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 120px;
    min-width: 0;
    width: 100%;
    height: auto;
    min-height: 48px;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    font-family: ${TOKENS.font.sans};
    font-size: max(14px, 1rem);
    font-weight: 500;
    line-height: 1.25;
    color: ${TOKENS.color.foregroundSecondary};
    outline: none !important;
    box-shadow: none !important;

    &::placeholder {
      color: ${TOKENS.color.foregroundMuted};
    }

    &:focus,
    &:focus-visible {
      outline: none !important;
      box-shadow: none !important;
      border: none !important;
    }
  }
`;

const ZipBarSubmitButton = styled.button<{ $compact?: boolean }>`
  align-self: center;
  margin: 0;
  min-height: 48px;
  text-wrap: nowrap;
  flex-grow: 0;
  flex-shrink: 0;
  height: auto;
  border-radius: ${TOKENS.radius.sm};
  padding: ${TOKENS.space[12]}
    ${({ $compact }) => ($compact ? TOKENS.space[12] : TOKENS.space[24])};
  font-family: ${TOKENS.font.sans};
  font-size: ${({ $compact }) => ($compact ? 'max(12px, 0.875rem)' : '1rem')};
  font-weight: ${TOKENS.type.bodyMdWeight};
  line-height: 1.25;
  text-transform: none;
  white-space: normal;
  text-align: center;
  background: ${TOKENS.color.primary};
  border: 1px solid ${TOKENS.color.primary};
  color: ${TOKENS.color.primaryForeground};
  cursor: pointer;
  transition: filter 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(0.9);
  }

  &:active:not(:disabled) {
    filter: brightness(0.75);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid ${TOKENS.color.ring};
    outline-offset: 2px;
  }
`;

const HomeZipBarSubmitButton: React.FC<{
  children: React.ReactNode;
  compact?: boolean;
}> = ({ children, compact }) => {
  const { loading } = useFunnelEntryPointContext();

  return (
    <ZipBarSubmitButton type="submit" disabled={loading} $compact={compact}>
      {children}
    </ZipBarSubmitButton>
  );
};

const HeroErrorBanner = styled.div<{ $invalid?: boolean }>`
  margin-top: -1px;
  border-radius: 0 0 ${TOKENS.radius.md} ${TOKENS.radius.md};
  background: ${({ $invalid }) =>
    $invalid ? 'rgba(209, 67, 67, 0.2)' : TOKENS.color.errorBanner};
  padding: ${TOKENS.space[12]} ${TOKENS.space[24]};
  font-family: ${TOKENS.font.sans};
  font-size: max(14px, 0.9375rem);
  line-height: 1.4;
  font-weight: 500;
  overflow-wrap: anywhere;
  color: ${({ $invalid }) =>
    $invalid ? TOKENS.color.error : TOKENS.color.onDark};
`;

const FooterErrorText = styled.p<{
  $invalid?: boolean;
  $surface: HomeZipBarSurface;
}>`
  margin: ${TOKENS.space[8]} 0 0;
  font-family: ${TOKENS.font.sans};
  font-size: max(14px, 0.9375rem);
  line-height: 1.4;
  font-weight: 500;
  overflow-wrap: anywhere;
  color: ${({ $invalid, $surface }) => {
    if ($invalid) return TOKENS.color.error;
    return $surface === 'dark'
      ? TOKENS.color.onDark
      : TOKENS.color.foregroundMuted;
  }};
`;

const FooterErrorBanner = styled.div<{ $invalid?: boolean }>`
  margin-top: ${TOKENS.space[8]};
  border-radius: ${TOKENS.radius.sm};
  background: ${({ $invalid }) =>
    $invalid ? 'rgba(209, 67, 67, 0.2)' : TOKENS.color.errorBanner};
  padding: ${TOKENS.space[12]} ${TOKENS.space[16]};
  font-family: ${TOKENS.font.sans};
  font-size: max(14px, 0.9375rem);
  line-height: 1.4;
  font-weight: 500;
  overflow-wrap: anywhere;
  color: ${({ $invalid }) =>
    $invalid ? TOKENS.color.error : TOKENS.color.onDark};
`;

const LocationsLink = styled.a<{
  $invalid?: boolean;
  $variant: HomeZipBarVariant;
}>`
  color: inherit;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;

  ${({ $invalid, $variant }) =>
    !$invalid &&
    $variant === 'hero' &&
    css`
      text-decoration: underline;

      ${MEDIA.lg} {
        text-decoration: none;
      }
    `}

  &:hover {
    text-decoration: underline;
  }
`;

const scrollToLocations = (event: React.MouseEvent<HTMLAnchorElement>) => {
  event.preventDefault();
  const el = document.getElementById('locations');
  if (!el) {
    window.location.hash = 'locations';
    return;
  }

  const header = document.querySelector('header');
  const offset = (header?.getBoundingClientRect().height ?? 98) + 8;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  scrollToY(top);
  history.replaceState(null, '', '#locations');
};

const HomeZipBarError: React.FC<{
  variant: HomeZipBarVariant;
  surface: HomeZipBarSurface;
}> = ({ variant, surface }) => {
  const { errorMessage, errorKind } = useFunnelEntryPointContext();

  if (!errorMessage || !errorKind) return null;

  const isInvalid = errorKind === 'invalid';
  const locationsLink = (
    <LocationsLink
      href="#locations"
      $invalid={isInvalid}
      $variant={variant}
      onClick={scrollToLocations}
    >
      View all locations
    </LocationsLink>
  );

  if (variant === 'hero') {
    return (
      <HeroErrorBanner $invalid={isInvalid}>
        {isInvalid ? (
          'Please enter a valid ZIP code.'
        ) : (
          <>We don&apos;t serve this area yet. {locationsLink}</>
        )}
      </HeroErrorBanner>
    );
  }

  if (surface === 'dark') {
    return (
      <FooterErrorBanner $invalid={isInvalid}>
        {isInvalid ? (
          'Please enter a valid ZIP code.'
        ) : (
          <>We don&apos;t serve this area yet. {locationsLink}</>
        )}
      </FooterErrorBanner>
    );
  }

  return (
    <FooterErrorText $invalid={isInvalid} $surface={surface}>
      {isInvalid ? (
        'Please enter a valid ZIP code.'
      ) : (
        <>We don&apos;t serve this area yet. {locationsLink}</>
      )}
    </FooterErrorText>
  );
};

const HomeZipBarFields: React.FC<{
  cta?: React.ReactNode;
  placeholder?: string;
  compact?: boolean;
  variant: HomeZipBarVariant;
  surface: HomeZipBarSurface;
}> = ({ cta, placeholder, compact, variant, surface }) => {
  const { errorKind } = useFunnelEntryPointContext();
  const hasError = errorKind === 'invalid';
  const attachedHeroError = variant === 'hero' && errorKind === 'osa';
  const zipInputProps =
    placeholder !== undefined
      ? {
          placeholder,
          shortenPlaceholderText: false as const,
        }
      : {};

  return (
    <ZipBarShell $compact={compact}>
      <ZipBar
        $compact={compact}
        $hasError={hasError}
        $attachedError={attachedHeroError}
      >
        <FunnelEntryPointZipInput {...zipInputProps} />
        <HomeZipBarSubmitButton compact={compact}>{cta}</HomeZipBarSubmitButton>
      </ZipBar>
      <HomeZipBarError variant={variant} surface={surface} />
    </ZipBarShell>
  );
};

type HomeZipBarProps = {
  id: string;
  className?: string;
  cta?: React.ReactNode;
  placeholder?: string;
  compact?: boolean;
  container: string;
  variant?: HomeZipBarVariant;
  surface?: HomeZipBarSurface;
};

const ContinueBookingButton = styled(PrimaryButton)`
  width: 100%;
  justify-content: center;
  font-size: 16px;
  padding: ${TOKENS.space[12]} ${TOKENS.space[24]};
`;

const HomeZipBarContinue: React.FC<{
  className?: string;
  compact?: boolean;
  container: string;
}> = ({ className, compact, container }) => {
  const { data, loading } = useQuery(DraftDocument);
  const draft = data?.draft;
  const activeDraft = draft && hasHomeDraft(draft) ? draft : null;
  const { resume } = useDraftResume(activeDraft, container);

  if (loading || !activeDraft) {
    return null;
  }

  return (
    <ZipBarShell className={className} $compact={compact}>
      <ContinueBookingButton as="button" type="button" onClick={() => resume()}>
        Continue booking
      </ContinueBookingButton>
    </ZipBarShell>
  );
};

export const HomeZipBar: React.FC<HomeZipBarProps> = ({
  id,
  className,
  cta = 'Get started',
  placeholder = 'Enter ZIP',
  compact,
  container,
  variant = 'hero',
  surface = 'dark',
}) => {
  const { data, loading } = useQuery(DraftDocument);
  const draft = data?.draft;

  if (variant !== 'hero') {
    if (!loading && draft && hasHomeDraft(draft)) {
      return (
        <HomeZipBarContinue
          className={className}
          compact={compact}
          container={container}
        />
      );
    }
  }

  return (
    <FunnelEntryPoint
      service={ServiceSelection.Storage}
      wtConfig={{ container }}
      emptyZipRedirectTo={WWW_ROUTES.PRODUCT_PAGE_STORAGE}
    >
      <FunnelEntryPointForm className={className} id={id}>
        <HomeZipBarFields
          cta={cta}
          placeholder={placeholder}
          compact={compact}
          variant={variant}
          surface={surface}
        />
      </FunnelEntryPointForm>
    </FunnelEntryPoint>
  );
};
